import { ReactElement, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { useField } from 'formik';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { raiseErrorBanner } from 'components/MessageDispatch/messageDispatch';
import { clearMessages } from 'store/ancillary/actions';
import { debounceAsync } from 'utils/misc/debounce';
import CustomFormControl from '../base/CustomFormControl';
import RequiredIndicator from 'pages/ApplicationRequest/components/containers/RequiredIndicator';
import { searchNonEmployeeJobTitles } from 'pages/NewNonEmployee/_api';
import {
	BEM_NAME_ELEMENTS,
	NEW_NON_EMPLOYEE_KEYS,
} from 'pages/NewNonEmployee/_const';
import CustomHelperText from '../base/CustomHelperText';

/**
 * Prompt user for input: Third-Party Organization
 * @returns {ReactElement} orm field to select a third-party organization
 */
const NonEmployeeJobTitle = () => {
	return (
		<NonEmployeeJobTitleAutoComplete
			name={NEW_NON_EMPLOYEE_KEYS.JOB_TITLE}
			label='Job Title'
			required
		/>
	);
};

/**
 * Prompt user for input: Third-Party organization (type and search)
 * @param {any} helperText field helper text
 * @param {string} label field label
 * @param {any} props properties to pass to form field
 * @returns {ReactElement} auto-complete form field to select a third-party organization
 */
const NonEmployeeJobTitleAutoComplete = ({ label, ...props }) => {
	const [field, meta, helpers] = useField(props);
	const [sLoading, setLoading] = useState(false);
	const [options, setOptions] = useState([]);
	const [sInputValue, setInputValue] = useState(
		field.value?.jobTitleName ?? ''
	);
	const dispatch = useDispatch();
	const [sHasSearched, setHasSearched] = useState(false);

	/**
	 * Memoized function required in order to preserve timeout IDs, otherwise function will be created new each render.
	 */
	const debouncedSearch = useMemo(() => {
		return debounceAsync(async input => {
			try {
				const processedInput = String(input).trim();
				const fetchResults = await searchNonEmployeeJobTitles(
					processedInput
				);
				let optionsList = [];
				if (typeof fetchResults === 'object') {
					const options = Object.keys(fetchResults)
						.map(key => ({
							key: key,
							value: fetchResults[key],
						}))
						.sort((a, b) => {
							if (a.key < b.key) return -1;
							if (a.key > b.key) return 1;
							return 0;
						});
					optionsList =
						options.length > 200 ? options.slice(0, 200) : options;
				}

				setOptions(optionsList);
			} catch (ex) {
				dispatch(clearMessages());
				raiseErrorBanner(ex, dispatch);
			} finally {
				setLoading(false);
			}
		});
	}, [dispatch]);

	return (
		<CustomFormControl>
			<Autocomplete
				autoComplete
				value={field.value?.jobTitleName || null}
				inputValue={sInputValue}
				clearIcon={null}
				disableClearable
				filterOptions={options => options}
				forcePopupIcon={true}
				getOptionLabel={option => (option.key ? option.key : option)}
				isOptionEqualToValue={(option, value) => {
					//option and key are key-value-pair objects where the value is an object of shape {jobTitleId, jobTitleName}
					return (
						option.value?.jobTitleName === value ||
						option.value?.jobTitleId === value.value?.jobTitleId
					);
				}}
				loading={sLoading}
				loadingText='Searching...'
				noOptionsText={sHasSearched ? 'No results' : 'Type to search'}
				onChange={(_, newValue) => {
					if (newValue) {
						helpers.setValue(newValue.value);
					} else {
						helpers.setValue(null);
					}
				}}
				onInputChange={(e, newInputValue, reason) => {
					if (e) {
						setInputValue(newInputValue);
						helpers.setValue(null);
						if (reason === 'input') {
							setLoading(true);
							debouncedSearch(newInputValue);
							setHasSearched(!!newInputValue);
						}
					}
				}}
				options={options}
				renderInput={params => (
					<TextField
						{...params}
						label={
							<>
								<RequiredIndicator />
								{label}
							</>
						}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									<InputAdornment
										sx={{
											marginBottom: '8px',
										}}
										position='end'
									>
										{sLoading ? (
											<CircularProgress
												size={20}
												variant='indeterminate'
											/>
										) : null}
									</InputAdornment>
									{params?.InputProps?.endAdornment}
								</>
							),
						}}
						error={meta.touched && !!meta.error}
						onBlur={field.onBlur}
						onFocus={field.onFocus}
						{...props}
						inputProps={{
							...params.inputProps,
							id: `${BEM_NAME_ELEMENTS.VIEW_FORM}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${props.name}`,
						}}
					/>
				)}
				renderOption={(props, option, { inputValue }) => {
					const optionStr = option.key ? option.key : option;
					const matches = match(optionStr, inputValue, {
						insideWords: true,
						findAllOccurrences: true,
					});
					const parts = parse(optionStr, matches);

					return (
						<li {...props}>
							<div
								id={`${BEM_NAME_ELEMENTS.VIEW_FORM}__${BEM_NAME_ELEMENTS.FIELD_OPTIONS}--${optionStr}`}
							>
								{parts.map((part, index) => (
									<span
										key={index}
										style={{
											fontWeight: part.highlight
												? 700
												: 400,
										}}
									>
										{part.text}
									</span>
								))}
							</div>
						</li>
					);
				}}
				onBlur={field.onBlur}
				onFocus={field.onFocus}
			/>
			<CustomHelperText error={meta.error} touched={meta.touched} />
		</CustomFormControl>
	);
};
export default NonEmployeeJobTitle;
